import { useState, useEffect } from 'react';
import { getAll, add, update, remove, STORES } from '../db';
import { Search, Plus, ChevronDown, ChevronUp, Trash2, Edit3, Save, X, ExternalLink } from 'lucide-react';

const emptySupplier = {
  name: '', type: '', brands: '', contact: '', email: '', phone: '',
  accountNo: '', website: '', loginDetails: '', howToOrder: '',
  paymentTerms: '', deliveryCost: '', minOrder: '', notes: '',
};

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptySupplier);
  const [showNew, setShowNew] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    const data = await getAll(STORES.suppliers);
    setSuppliers(data.sort((a, b) => a.name?.localeCompare(b.name)));
  };

  const filtered = suppliers.filter(s =>
    `${s.name} ${s.brands} ${s.type}`.toLowerCase().includes(search.toLowerCase())
  );

  const saveNew = async () => {
    if (!form.name) return;
    await add(STORES.suppliers, form);
    setForm(emptySupplier);
    setShowNew(false);
    load();
  };

  const saveEdit = async () => {
    await update(STORES.suppliers, editing);
    setEditing(null);
    load();
  };

  const deleteSupplier = async (id) => {
    if (!window.confirm('Delete this supplier?')) return;
    await remove(STORES.suppliers, id);
    setExpanded(null);
    load();
  };

  const SupplierForm = ({ data, setData, onSave, onCancel }) => (
    <div className="patient-form">
      <div className="form-section-title">Supplier Details</div>
      <div className="form-grid">
        <div className="form-field"><label>Supplier Name *</label><input value={data.name} onChange={e => setData({ ...data, name: e.target.value })} /></div>
        <div className="form-field"><label>Type</label><input value={data.type} onChange={e => setData({ ...data, type: e.target.value })} placeholder="Online Pharmacy, Distributor..." /></div>
        <div className="form-field full"><label>Brands / Products</label><input value={data.brands} onChange={e => setData({ ...data, brands: e.target.value })} placeholder="e.g. Tebiskin, Enerpeels, SkinMed" /></div>
        <div className="form-field"><label>Contact Name</label><input value={data.contact} onChange={e => setData({ ...data, contact: e.target.value })} /></div>
        <div className="form-field"><label>Email</label><input value={data.email} onChange={e => setData({ ...data, email: e.target.value })} /></div>
        <div className="form-field"><label>Phone</label><input value={data.phone} onChange={e => setData({ ...data, phone: e.target.value })} /></div>
        <div className="form-field"><label>Account Number</label><input value={data.accountNo} onChange={e => setData({ ...data, accountNo: e.target.value })} /></div>
        <div className="form-field"><label>Website</label><input value={data.website} onChange={e => setData({ ...data, website: e.target.value })} placeholder="https://..." /></div>
      </div>
      <div className="form-section-title" style={{ marginTop: '1rem' }}>Ordering</div>
      <div className="form-grid">
        <div className="form-field full"><label>Login / Access Details</label><textarea value={data.loginDetails} onChange={e => setData({ ...data, loginDetails: e.target.value })} rows={2} placeholder="Username, password hints, portal URL..." /></div>
        <div className="form-field full"><label>How to Order</label><textarea value={data.howToOrder} onChange={e => setData({ ...data, howToOrder: e.target.value })} rows={2} placeholder="Online / phone / email / order form..." /></div>
        <div className="form-field"><label>Payment Terms</label><input value={data.paymentTerms} onChange={e => setData({ ...data, paymentTerms: e.target.value })} placeholder="e.g. 30 days, card on order" /></div>
        <div className="form-field"><label>Delivery Cost</label><input value={data.deliveryCost} onChange={e => setData({ ...data, deliveryCost: e.target.value })} placeholder="e.g. £6.95 / Free over £25" /></div>
        <div className="form-field"><label>Min Order</label><input value={data.minOrder} onChange={e => setData({ ...data, minOrder: e.target.value })} /></div>
        <div className="form-field full"><label>Notes</label><textarea value={data.notes} onChange={e => setData({ ...data, notes: e.target.value })} rows={2} /></div>
      </div>
      <div className="form-actions">
        <button className="btn-ghost" onClick={onCancel}><X size={14} /> Cancel</button>
        <button className="btn-gold" onClick={onSave}><Save size={14} /> Save Supplier</button>
      </div>
    </div>
  );

  return (
    <div className="section-content">
      <div className="section-header-row">
        <div>
          <h2 className="section-title">Suppliers</h2>
          <p className="section-sub">{suppliers.length} supplier{suppliers.length !== 1 ? 's' : ''}</p>
        </div>
        <button className="btn-gold" onClick={() => setShowNew(true)}><Plus size={14} /> Add Supplier</button>
      </div>

      <div className="search-bar">
        <Search size={15} className="search-icon" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search suppliers or brands..." className="search-input" />
      </div>

      {showNew && (
        <div className="patient-card expanded" style={{ marginBottom: '1rem' }}>
          <div className="card-header"><div className="patient-name-block"><div className="patient-initials">+</div><div className="patient-name">New Supplier</div></div></div>
          <SupplierForm data={form} setData={setForm} onSave={saveNew} onCancel={() => { setShowNew(false); setForm(emptySupplier); }} />
        </div>
      )}

      <div className="patient-list">
        {filtered.map(s => (
          <div key={s.id} className={`patient-card ${expanded === s.id ? 'expanded' : ''}`}>
            <div className="card-header" onClick={() => { setExpanded(expanded === s.id ? null : s.id); setEditing(null); }}>
              <div className="patient-name-block">
                <div className="patient-initials">{s.name?.[0]}{s.name?.split(' ')?.[1]?.[0] || ''}</div>
                <div>
                  <div className="patient-name">{s.name}</div>
                  <div className="patient-meta">
                    {s.type && <span>{s.type}</span>}
                    {s.brands && <span>{s.brands}</span>}
                    {s.accountNo && <span>Acc: {s.accountNo}</span>}
                  </div>
                </div>
              </div>
              <div className="card-actions" onClick={e => e.stopPropagation()}>
                {s.website && <a href={s.website} target="_blank" rel="noreferrer" className="icon-btn"><ExternalLink size={14} /></a>}
                <button className="icon-btn" onClick={() => { setEditing(s); setExpanded(s.id); }}><Edit3 size={14} /></button>
                <button className="icon-btn danger" onClick={() => deleteSupplier(s.id)}><Trash2 size={14} /></button>
                <span className="expand-icon">{expanded === s.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</span>
              </div>
            </div>
            {expanded === s.id && (
              <div className="card-body">
                {editing?.id === s.id ? (
                  <SupplierForm data={editing} setData={setEditing} onSave={saveEdit} onCancel={() => setEditing(null)} />
                ) : (
                  <div className="detail-grid">
                    <div className="detail-row"><span className="detail-label">Contact</span><span>{s.contact || '—'}</span></div>
                    <div className="detail-row"><span className="detail-label">Email</span><span>{s.email || '—'}</span></div>
                    <div className="detail-row"><span className="detail-label">Phone</span><span>{s.phone || '—'}</span></div>
                    <div className="detail-row"><span className="detail-label">Account No.</span><span>{s.accountNo || '—'}</span></div>
                    <div className="detail-row"><span className="detail-label">Payment Terms</span><span>{s.paymentTerms || '—'}</span></div>
                    <div className="detail-row"><span className="detail-label">Delivery</span><span>{s.deliveryCost || '—'}</span></div>
                    <div className="detail-row full"><span className="detail-label">Login Details</span><span>{s.loginDetails || '—'}</span></div>
                    <div className="detail-row full"><span className="detail-label">How to Order</span><span>{s.howToOrder || '—'}</span></div>
                    <div className="detail-row full"><span className="detail-label">Notes</span><span>{s.notes || '—'}</span></div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        {filtered.length === 0 && !showNew && (
          <div className="empty-state"><p>No suppliers yet</p><button className="btn-gold" onClick={() => setShowNew(true)}><Plus size={14} /> Add your first supplier</button></div>
        )}
      </div>
    </div>
  );
}
